/**
 * What the CRM's unattended machinery is doing.
 *
 * Mirrors apps/api/src/modules/crm-dashboard/background/. One request, three
 * lists, because the shell asks three questions: what is running, what went
 * wrong, and is anything alive at all.
 */
import { api } from "./api";

/**
 * An unattended run in flight.
 *
 * ⚠ `kind` is a MIRROR of `BgJob["kind"]` in
 * `apps/api/src/modules/crm-dashboard/background/types.ts`, and it is the half
 * that has already gone wrong once: the server started sending `brain` while
 * this union still listed six kinds, so `JOB_ICON[job.kind]` in `JobFace.tsx`
 * resolved to `undefined` and the whole shell died with React error #130. It is
 * a silent break by construction - a NARROWER client union is exactly what
 * stops `tsc` from complaining about the missing icon. `background.test.ts`
 * holds the two lists against each other for that reason.
 */
export interface BgJob {
  key: string;
  kind: "campaign" | "scanner" | "bulk" | "register" | "calls" | "geocode" | "brain";
  name: string;
  done: number;
  /** 0 when the run cannot know its size - show no meter. */
  total: number;
  /** Items waiting, for work that is a queue rather than a run. */
  pending?: number;
}

export type BgAlertId =
  | "transcripts-failed"
  | "recordings-failed"
  | "calls-failed"
  | "telephony-webhook-rejected"
  /** The day's AI budget is spent and transcripts are waiting for the refill. */
  | "ai-quota-waiting"
  | "scanner-run-failed"
  | "register-sync-failed"
  | "mail-send-failed";

/** Something that ended badly and nobody has looked. */
export interface BgAlert {
  key: string;
  id: BgAlertId;
  count: number;
  at: string;
  detail: string | null;
}

export type BgEngine = "scanner" | "calls" | "mail" | "register" | "monitoring" | "monitoringJobs";

/** One engine's pulse. */
export interface BgBeat {
  key: BgEngine;
  at: string | null;
  ok: boolean;
  note: string | null;
  enabled: boolean;
  /** On, healthy, and out of budget until it resets. Not the same as off. */
  resting?: boolean;
}

export interface BackgroundPayload {
  jobs: BgJob[];
  attention: BgAlert[];
  beats: BgBeat[];
}

export const EMPTY_BACKGROUND: BackgroundPayload = { jobs: [], attention: [], beats: [] };

/** `transcripts-failed` -> `transcriptsFailed`, so a server enum addresses a key. */
export const bgKey = (id: string): string => id.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());

export const getBackground = (): Promise<BackgroundPayload> =>
  api.get<BackgroundPayload>("/admin/crm/background");
